import React, { useEffect, useState } from "react";
import { Modal, Button, Form, Row, Col, Accordion } from "react-bootstrap";
import { toast } from "react-toastify";
import axios from "axios";

const BookingFormModal = ({
  show,
  onHide,
  onClose,
  rooms,
  selectedRoom,
  selectedDate,
  selectedSlot,
  onSubmit,
  errors,
  hostName,
  isAdmin,
  bookingData,
  token // Add token prop to access API
}) => {

  const [formData, setFormData] = useState({
    roomId: "",
    timeFrom: "",
    timeTo: "",
    people: "",
    hostName: "",
    participants: "",
    bookingSubject: "",
    bookingDescription: "",
    allocationDate: ""
  });

  // Add state for reschedule mode
  const [isRescheduleMode, setIsRescheduleMode] = useState(false);
  const [rescheduleData, setRescheduleData] = useState({
    allocation_date: "",
    time_from: "",
    time_to: ""
  });
  
  // Add state for loading
  const [isLoading, setIsLoading] = useState(false);

  console.log("host", hostName)
  
  /** -------------------------------
   * Auto-fill default values
   -------------------------------- */
  useEffect(() => {
    // Auto-fill when modal opens or related props change
    setFormData(prev => ({
      ...prev,
      hostName: hostName || prev.hostName, // ✅ prefill hostName
    }));

    if (selectedRoom?.roomId) {
      setFormData(prev => ({
        ...prev,
        roomId: selectedRoom.roomId,
      }));
    }

    if (selectedSlot) {
      const from = selectedSlot;
      const to = addMinutes(from, 15);
      setFormData(prev => ({
        ...prev,
        timeFrom: from,
        timeTo: to,
      }));
    }

    if (selectedDate) {
      setFormData(prev => ({
        ...prev,
        allocationDate: selectedDate,
      }));
    }

    // If bookingData exists and is a single booking, prefill form for reschedule
    if (isAdmin && bookingData && !Array.isArray(bookingData)) {
      setFormData(prev => ({
        ...prev,
        roomId: bookingData.roomId || prev.roomId,
        timeFrom: bookingData.timeFrom || prev.timeFrom,
        timeTo: bookingData.timeTo || prev.timeTo,
        people: bookingData.peopleCount || bookingData.people || prev.people,
        hostName: bookingData.hostName || prev.hostName,
        participants: Array.isArray(bookingData.participants) 
          ? bookingData.participants.join(", ") 
          : bookingData.participants || prev.participants,
        bookingSubject: bookingData.bookingSubject || prev.bookingSubject,
        bookingDescription: bookingData.bookingDescription || prev.bookingDescription,
        allocationDate: bookingData.allocationDate?.split("T")[0] || prev.allocationDate
      }));

      // Initialize reschedule data
      setRescheduleData({
        allocation_date: bookingData.allocationDate?.split("T")[0] || "",
        time_from: bookingData.timeFrom || "",
        time_to: bookingData.timeTo || ""
      });
    }

  }, [hostName, selectedRoom, selectedSlot, selectedDate, bookingData, isAdmin]);

  const isMultiple = Array.isArray(bookingData) && bookingData.length > 1;

  /** -------------------------------
   * Helpers
   -------------------------------- */
  const addMinutes = (time, mins) => {
    const [h, m] = time.split(":").map(Number);
    const d = new Date(2000, 0, 1, h, m + mins);
    return d.toTimeString().slice(0, 5);
  };

  const convertMinutesToLabel = (mins) => {
    if (mins < 60) return `${mins} mins`;
    if (mins % 60 === 0) return `${mins / 60} hrs`;
    return `${Math.floor(mins / 60)} hrs ${mins % 60} mins`;
  };

  /** -------------------------------
   * Input Change
   -------------------------------- */
  const validateTimeDifference = (from, to) => {
    if (!from || !to) return true;

    const start = new Date(`1970-01-01T${from}:00`);
    const end = new Date(`1970-01-01T${to}:00`);

    const diffInMinutes = (end - start) / (1000 * 60);

    // ❗ timeTo cannot be before timeFrom
    if (diffInMinutes < 0) {
      toast.error("Time To cannot be earlier than Time From");
      return false;
    }

    // ❗ must be minimum 15 minutes
    if (diffInMinutes < 15) {
      toast.error("Minimum duration should be 15 minutes");
      return false;
    }

    return true;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;

    setFormData((prev) => {
      let updated = { ...prev, [name]: value };

      if (name === "roomId") {
        const selectedRoom = rooms.find(room => room.roomId == value); // use == or Number(value)

        if (selectedRoom) {
          updated = {
            ...updated,
            capacity: selectedRoom.capacity,
            timeLimit: convertMinutesToLabel(selectedRoom.timeLimitMinutes),
          };
        } else {
          updated = {
            ...updated,
            capacity: "",
            timeLimit: "",
          };
        }
      }

      if (name === "timeFrom" || name === "timeTo") {
        validateTimeDifference(updated.timeFrom, updated.timeTo);
      }

      return updated;
    });

    // Update reschedule data if in reschedule mode
    if (isRescheduleMode) {
      if (name === "allocationDate") {
        setRescheduleData(prev => ({
          ...prev,
          allocation_date: value
        }));
      } else if (name === "timeFrom") {
        setRescheduleData(prev => ({
          ...prev,
          time_from: value + ":00"
        }));
      } else if (name === "timeTo") {
        setRescheduleData(prev => ({
          ...prev,
          time_to: value + ":00"
        }));
      }
    }
  };

  useEffect(() => {
    console.log("BookingFormModal props:", { isAdmin, bookingData });
  }, [isAdmin, bookingData]);

  /** -------------------------------
   * Cancel Booking Function
   -------------------------------- */
  const handleCancelBooking = async () => {
    // Get booking ID from bookingData
    const bookingId = bookingData?.bookingId || bookingData?.id;
    
    if (!bookingId) {
      toast.error("Booking ID not found");
      return;
    }

    // Confirm before cancelling
    const confirmed = window.confirm("Are you sure you want to cancel this booking?");
    if (!confirmed) return;

    setIsLoading(true);
    try {
      const response = await axios.delete(
        `https://dotconnect.dotandkey.com/api/v1/room/bookings/${bookingId}/cancel`,
        {
          headers: {
            'accept': 'application/json',
            'Authorization': `Bearer ${token}`,
          }
        }
      );

      if (response.status === 200) {
        toast.success("Booking cancelled successfully!");
        onClose(); // Close modal after successful cancellation
        // You might want to refresh the bookings list here
        if (typeof window.refreshBookings === 'function') {
          window.refreshBookings();
        }
      }
    } catch (error) {
      console.error("Cancel booking error:", error);
      toast.error(error.response?.data?.message || "Failed to cancel booking");
    } finally {
      setIsLoading(false);
    }
  };

  /** -------------------------------
   * Reschedule Function
   -------------------------------- */
  const handleReschedule = async () => {
    // Get booking ID from bookingData
    const bookingId = bookingData?.bookingId || bookingData?.id;
    
    if (!bookingId) {
      toast.error("Booking ID not found");
      return;
    }

    // Validate reschedule data
    if (!rescheduleData.allocation_date) {
      toast.error("Date is required for rescheduling");
      return;
    }
    if (!rescheduleData.time_from) {
      toast.error("Start time is required for rescheduling");
      return;
    }
    if (!rescheduleData.time_to) {
      toast.error("End time is required for rescheduling");
      return;
    }

    // Validate time difference
    const fromTime = rescheduleData.time_from.slice(0, 5);
    const toTime = rescheduleData.time_to.slice(0, 5);
    if (!validateTimeDifference(fromTime, toTime)) {
      return;
    }

    setIsLoading(true);
    try {
      const response = await axios.put(
        `https://dotconnect.dotandkey.com/api/v1/room/bookings/${bookingId}/reschedule`,
        rescheduleData,
        {
          headers: {
            'accept': 'application/json',
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      if (response.status === 200) {
        toast.success("Booking rescheduled successfully!");
        setIsRescheduleMode(false);
        onClose(); // Close modal after successful reschedule
        // You might want to refresh the bookings list here
        if (typeof window.refreshBookings === 'function') {
          window.refreshBookings();
        }
      }
    } catch (error) {
      console.error("Reschedule error:", error);
      toast.error(error.response?.data?.message || "Failed to reschedule booking");
    } finally {
      setIsLoading(false);
    }
  };

  /** -------------------------------
   * Validation
   -------------------------------- */
  const validate = () => {
    if (!formData.hostName) {
      toast.error("Host name required.");
      return false;
    }
    if (!formData.roomId) {
      toast.error("Room is required.");
      return false;
    }
    if (!formData.allocationDate) {
      toast.error("Booking date required.");
      return false;
    }
    if (!formData.timeFrom) {
      toast.error("Start time required.");
      return false;
    }
    if (!formData.timeTo) {
      toast.error("End time required.");
      return false;
    }
    if (!formData.people) {
      toast.error("People count required.");
      return false;
    }

    if (!formData.bookingSubject) {
      toast.error("Subject required.");
      return false;
    }

    return true;
  };

  const [otp, setOtp] = useState(false);
  const handleSubmit = async () => {
    const isValid = validate(); // local field check
    if (!isValid) return;

    try {
      const isBookingValid = await onSubmit(formData); // parent validation (capacity/time)
      if (isBookingValid) {
        setOtp(true);
        toast.success("Booking submitted successfully!");
      }
    } catch (error) {
      toast.error("Failed to submit booking.");
    }
  };

  const selectedRoomData = rooms.find(r => r.roomId == formData.roomId);
  const maxTimeLabel = selectedRoomData
    ? convertMinutesToLabel(selectedRoomData.timeLimitMinutes)
    : "";

  return (
    <Modal show={show} onHide={onHide} centered size={isMultiple ? "lg" : "md"}>
      {/* ---------------- HEADER ---------------- */}
      <Modal.Header closeButton>
        <Modal.Title>
          {isAdmin && bookingData
            ? isMultiple
              ? `Multiple Bookings (${bookingData.length})`
              : "Booking Details"
            : "Book Room"}
        </Modal.Title>
        
        {/* Action buttons for admin viewing single booking */}
      
      </Modal.Header>

      {/* ---------------- BODY ---------------- */}
      <Modal.Body>
        {/* --- ADMIN MULTIPLE BOOKINGS (Accordion view) --- */}
        {isAdmin && Array.isArray(bookingData) && bookingData.length > 1 ? (
          <Accordion defaultActiveKey="0">
            {bookingData.map((booking, idx) => (
              <Accordion.Item eventKey={String(idx)} key={idx}>
                <Accordion.Header>
                  {booking.roomNumber} | {booking.timeFrom} - {booking.timeTo} | {booking.hostName}
                </Accordion.Header>
                <Accordion.Body>
                  <Row>
                    <Col md={6} className="mb-2">
                      <strong>Room No:</strong> {booking.roomNumber}
                    </Col>
                    <Col md={6} className="mb-2">
                      <strong>Host Name:</strong> {booking.hostName}
                    </Col>
                    <Col md={6} className="mb-2">
                      <strong>Date:</strong> {booking.allocationDate?.split("T")[0] || "-"}
                    </Col>
                    <Col md={6} className="mb-2">
                      <strong>Time:</strong> {booking.timeFrom} - {booking.timeTo}
                    </Col>
                    <Col md={6} className="mb-2">
                      <strong>No. of People:</strong> {booking.peopleCount || booking.people}
                    </Col>
                    <Col md={12} className="mb-2">
                      <strong>Participants:</strong>{" "}
                      {Array.isArray(booking.participants)
                        ? booking.participants.join(", ")
                        : booking.participants || "-"}
                    </Col>
                    <Col md={12} className="mb-2">
                      <strong>Subject:</strong> {booking.bookingSubject || "-"}
                    </Col>
                    <Col md={12} className="mb-2">
                      <strong>Description:</strong> {booking.bookingDescription || "-"}
                    </Col>
                  </Row>
                </Accordion.Body>
              </Accordion.Item>
            ))}
          </Accordion>
        ) : isAdmin && bookingData ? (
          /* --- ADMIN SINGLE BOOKING DETAIL --- */
          <>
            {isRescheduleMode ? (
              /* --- RESCHEDULE FORM --- */
              <Row>
                <Col md={6} className="mb-3">
                  <strong>Room No:</strong> {bookingData.roomNumber}
                </Col>
                <Col md={6} className="mb-3">
                  <strong>Host Name:</strong> {bookingData.hostName}
                </Col>
                
                <Col md={6} className="mt-2">
                  <Form.Label>Date</Form.Label>
                  <Form.Control
                    type="date"
                    name="allocationDate"
                    value={formData.allocationDate}
                    onChange={handleChange}
                    disabled={isLoading}
                  />
                </Col>

                <Col md={3} className="mt-2">
                  <Form.Label>Time From</Form.Label>
                  <Form.Control
                    type="time"
                    name="timeFrom"
                    value={formData.timeFrom}
                    onChange={handleChange}
                    disabled={isLoading}
                  />
                </Col>

                <Col md={3} className="mt-2">
                  <Form.Label>Time To</Form.Label>
                  <Form.Control
                    type="time"
                    name="timeTo"
                    value={formData.timeTo}
                    onChange={handleChange}
                    disabled={isLoading}
                  />
                </Col>

                <Col md={6} className="mb-3">
                  <strong>No. of People:</strong> {bookingData.peopleCount}
                </Col>
                <Col md={12} className="mb-3">
                  <strong>Participants:</strong>{" "}
                  {Array.isArray(bookingData.participants)
                    ? bookingData.participants.join(", ")
                    : bookingData.participants || "-"}
                </Col>
                <Col md={12} className="mb-3">
                  <strong>Subject:</strong> {bookingData.bookingSubject || "-"}
                </Col>
                <Col md={12} className="mb-3">
                  <strong>Description:</strong> {bookingData.bookingDescription || "-"}
                </Col>
              </Row>
            ) : (
              /* --- VIEW MODE --- */
              <Row>
                <Col md={6} className="mb-3">
                  <strong>Room No:</strong> {bookingData.roomNumber}
                </Col>
                <Col md={6} className="mb-3">
                  <strong>Host Name:</strong> {bookingData.hostName}
                </Col>
                <Col md={6} className="mb-3">
                  <strong>Date:</strong>{" "}
                  {bookingData.allocationDate?.split("T")[0] || "-"}
                </Col>
                <Col md={6} className="mb-3">
                  <strong>Time:</strong> {bookingData.timeFrom} - {bookingData.timeTo}
                </Col>
                <Col md={6} className="mb-3">
                  <strong>No. of People:</strong> {bookingData.peopleCount}
                </Col>
                <Col md={12} className="mb-3">
                  <strong>Participants:</strong>{" "}
                  {Array.isArray(bookingData.participants)
                    ? bookingData.participants.join(", ")
                    : bookingData.participants || "-"}
                </Col>
                <Col md={12} className="mb-3">
                  <strong>Subject:</strong> {bookingData.bookingSubject || "-"}
                </Col>
                <Col md={12} className="mb-3">
                  <strong>Description:</strong> {bookingData.bookingDescription || "-"}
                </Col>
              </Row>
            )}
          </>
        ) : (
          /* --- BOOKING FORM (user or admin creating new) --- */
          <Row>
            <Col md={12} className="mt-2">
              <Form.Label>Host Name</Form.Label>
              <Form.Control
                name="hostName"
                value={formData.hostName}
                onChange={handleChange}
                disabled={!isAdmin}
              />
            </Col>

            <Col md={4} className="mt-2">
              <Form.Label>Room No</Form.Label>
              <Form.Select
                name="roomId"
                value={formData.roomId}
                onChange={handleChange}
              >
                <option value="">Select Room</option>
                {rooms.map((room) => (
                  <option key={room.roomId} value={room.roomId}>
                    {room.roomNumber}
                  </option>
                ))}
              </Form.Select>
            </Col>

            <Col md={4} className="mt-2">
              <Form.Label>Capacity</Form.Label>
              <Form.Control
                type="text"
                name="capacity"
                value={formData.capacity || ""}
                disabled
              />
            </Col>

            <Col md={4} className="mt-2">
              <Form.Label>Time Limit</Form.Label>
              <Form.Control
                type="text"
                name="timeLimit"
                value={formData.timeLimit || ""}
                disabled
              />
            </Col>

            <Col md={6} className="mt-2">
              <Form.Label>Time From</Form.Label>
              <Form.Control
                type="time"
                name="timeFrom"
                value={formData.timeFrom}
                onChange={handleChange}
              />
            </Col>

            <Col md={6} className="mt-2">
              <Form.Label>
                Time To <span className="text-muted">(Max: {maxTimeLabel})</span>
              </Form.Label>
              <Form.Control
                type="time"
                name="timeTo"
                value={formData.timeTo}
                onChange={handleChange}
              />
            </Col>

            <Col md={12} className="mt-2">
              <Form.Label>No. of People</Form.Label>
              <Form.Control
                type="number"
                name="people"
                value={formData.people}
                onChange={handleChange}
              />
            </Col>

            <Col md={12} className="mt-2">
              <Form.Label>Participants (emails)</Form.Label>
              <Form.Control
                name="participants"
                value={formData.participants}
                onChange={handleChange}
              />
            </Col>

            <Col md={12} className="mt-2">
              <Form.Label>Subject</Form.Label>
              <Form.Control
                name="bookingSubject"
                value={formData.bookingSubject}
                onChange={handleChange}
              />
            </Col>

            <Col md={12} className="mt-2">
              <Form.Label>Description</Form.Label>
              <Form.Control
                as="textarea"
                name="bookingDescription"
                value={formData.bookingDescription}
                onChange={handleChange}
              />
            </Col>
          </Row>
        )}
      </Modal.Body>

      {/* ---------------- FOOTER ---------------- */}
      <Modal.Footer>
          {isAdmin && bookingData && !Array.isArray(bookingData) && (
          <div style={{ display: 'flex', justifyContent:"space-between", flexDirection:"row", width:"100%", gap:10 }}>
            {isRescheduleMode ? (
              <Button 
                variant="secondary" 
                onClick={() => setIsRescheduleMode(false)}
                disabled={isLoading}
              >
                Cancel
              </Button>
            ) : (
              <>
                <Button 
                  variant="danger" 
                  onClick={handleCancelBooking}
                  disabled={isLoading}
                >
                  {isLoading ? "Cancelling..." : "Cancel Booking"}
                </Button>
                <Button 
                  variant="warning" 
                  onClick={() => setIsRescheduleMode(!isRescheduleMode)}
                  disabled={isLoading}
                >
                  Reschedule
                </Button>
              </>
            )}
          </div>
        )}
        <Button variant="secondary" onClick={onClose} disabled={isLoading}>
          Close
        </Button>

        {/* Show Reschedule Confirm button when in reschedule mode */}
        {isAdmin && bookingData && !Array.isArray(bookingData) && isRescheduleMode && (
          <Button variant="success" onClick={handleReschedule} disabled={isLoading}>
            {isLoading ? "Processing..." : "Confirm Reschedule"}
          </Button>
        )}

        {/* Show Book Now if new booking (user or admin) */}
        {(!bookingData || (Array.isArray(bookingData) && bookingData.length === 0)) && (
          <Button variant="primary" onClick={handleSubmit}>
            Book Now
          </Button>
        )}
      </Modal.Footer>
    </Modal>
  );
};

export default BookingFormModal;